import { LEDGER_SOURCE_LABEL, LedgerSource } from '@bariendo/lib/revenue-ledger';
import { TimelineItemType, TimelineRefundEvent } from '@/components/Timeline/types';
import { formatDollars } from '@/services/money';

/** A refund request as the bot returns it (BAR-3052). Dollars, positive. */
export interface RefundHistoryEntry {
  id: string;
  medplumPatientId: string;
  requestedAt: string;
  /** `Practitioner/<id>`, like the other `*By`. */
  requestedBy: string | null;
  /** Cancels the procedure: a Refund. Otherwise a Customer service fee. */
  cancel: boolean;
  reason: string;
  note: string | null;
  amountRequested: number;
  amountApproved: number | null;
  approvalNote: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  linkedAt: string | null;
  /** Null when the refund's arrival linked it. */
  linkedBy: string | null;
  linkedRefund: { amount: number; source: string | null; occurredAt: string } | null;
}

// Refund cards have no FHIR resource behind them, like the payment cards.
const NO_FHIR_REFERENCE = {};

function sourceLabel(source: string | null): string {
  if (!source) return '';
  return LEDGER_SOURCE_LABEL[source as LedgerSource] ?? source.charAt(0) + source.slice(1).toLowerCase();
}

/**
 * Each request's cards, as `timeline-shared.mdx` lists them: requested when
 * recorded, approved when Finance approves, issued when the refund that left is
 * linked. A cancellation with nothing to refund reads as the employer card,
 * `Cancelled after approval · <payor>`.
 */
export function mapRefundHistoryToTimelineItems(requests: RefundHistoryEntry[], payor?: string): TimelineRefundEvent[] {
  return requests.flatMap((request) => {
    const card = (
      kind: TimelineRefundEvent['kind'],
      title: string,
      at: string,
      recordedBy: string | null,
      note?: string | null
    ): TimelineRefundEvent => ({
      reference: NO_FHIR_REFERENCE,
      type: TimelineItemType.REFUND_EVENT,
      id: `${request.id}-${kind}`,
      kind,
      title,
      note: note || undefined,
      recordedBy: recordedBy ?? undefined,
      timestamp: new Date(at),
    });

    const reasonAndNote = [request.reason, request.note].filter(Boolean).join('. ');
    const cards = [
      request.amountRequested > 0
        ? card(
            'requested',
            `Refund requested · ${formatDollars(request.amountRequested)}${request.cancel ? ' · procedure cancelled' : ''}`,
            request.requestedAt,
            request.requestedBy,
            reasonAndNote
          )
        : card(
            'cancelled',
            ['Cancelled after approval', payor].filter(Boolean).join(' · '),
            request.requestedAt,
            request.requestedBy,
            reasonAndNote
          ),
    ];
    if (request.approvedAt && request.amountApproved !== null && request.amountApproved > 0) {
      cards.push(
        card(
          'approved',
          `Refund approved · ${formatDollars(request.amountApproved)}`,
          request.approvedAt,
          request.approvedBy,
          request.approvalNote
        )
      );
    }
    if (request.linkedAt && request.linkedRefund) {
      cards.push(
        card(
          'issued',
          `Refund issued · ${formatDollars(request.linkedRefund.amount)} via ${sourceLabel(request.linkedRefund.source)}`,
          request.linkedAt,
          request.linkedBy
        )
      );
    }
    return cards;
  });
}
